import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Clock, Loader2, Plus, Ban, Users, Baby, Save, X, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";
import { BookingPricingPanel, BookingRequestsInbox, ClosedBlocksPanel } from "./bookings/BookingPanels";

/* Private Bookings — admin. Open dates/times, block dates, view and edit
   bookings, set the status. Simple by design. */

const STATUS_STYLE = {
  pending:      { bg: "#F4D06F", fg: "#6E1E3A" },
  deposit_paid: { bg: "#C49247", fg: "#3C2A1E" },
  confirmed:    { bg: "#3E1F46", fg: "#FFFFFF" },
  paid:         { bg: "#2F6B4F", fg: "#FFFFFF" },
  cancelled:    { bg: "#A33030", fg: "#FFFFFF" },
  completed:    { bg: "#3C2A1E", fg: "#FFFFFF" },
};

const prettyDate = (iso) => iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";

export default function AdminPrivateBookings() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "bookings");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [slotDate, setSlotDate] = useState("");
  const [slotTimes, setSlotTimes] = useState("");
  const [blockDate, setBlockDate] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/private-bookings");
      setData(r.data);
    } catch { toast.error("Couldn't load private bookings."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addSlots = async () => {
    const times = slotTimes.split(",").map((t) => t.trim()).filter(Boolean);
    if (!slotDate || times.length === 0) return toast.error("Pick a date and add at least one time.");
    setBusy("slots");
    try {
      const r = await api.post("/admin/private-bookings/slots", { date: slotDate, times });
      toast.success(`${r.data.created.length} time${r.data.created.length === 1 ? "" : "s"} added.`);
      setSlotTimes("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add those times."); }
    finally { setBusy(""); }
  };

  const removeSlot = async (slot) => {
    setBusy(slot.id);
    try {
      await api.delete(`/admin/private-bookings/slots/${slot.id}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove that time."); }
    finally { setBusy(""); }
  };

  const block = async () => {
    if (!blockDate) return toast.error("Choose the date to block.");
    setBusy("block");
    try {
      await api.post("/admin/private-bookings/blocked-dates", { date: blockDate, reason: blockReason });
      toast.success(`${prettyDate(blockDate)} blocked.`);
      setBlockDate(""); setBlockReason("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't block that date."); }
    finally { setBusy(""); }
  };

  const unblock = async (b) => {
    setBusy(b.id);
    try {
      await api.delete(`/admin/private-bookings/blocked-dates/${b.id}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't unblock that date."); }
    finally { setBusy(""); }
  };

  const setStatus = async (booking, status) => {
    setBusy(booking.id);
    try {
      await api.patch(`/admin/private-bookings/bookings/${booking.id}`, { status });
      toast.success(`${booking.reference} marked ${status}.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update that booking."); }
    finally { setBusy(""); }
  };

  const slotsByDate = useMemo(() => {
    const map = new Map();
    for (const s of data?.slots || []) {
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push(s);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  if (loading) return <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading private bookings…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-private-bookings">
      <header>
        <div className="text-[10px] tracking-[0.42em] uppercase font-semibold text-[#6E1E3A]">✦ On the farm</div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-1 text-[#3C2A1E]">Private Bookings</h1>
        <p className="text-sm italic text-[#7A6A55] mt-1 max-w-2xl">
          Private Pickings run January to March. Open the dates and times you want, block anything you need, then confirm bookings as they come in.
        </p>
      </header>

      <div className="flex flex-wrap gap-2" data-testid="bookings-tabs">
        {[["bookings", "Bookings & dates"], ["requests", "Requests inbox"], ["blocks", "Repeating blocks"], ["pricing", "Prices & costs"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`bookings-tab-${k}`} className={"h-10 px-4 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold border " + (tab === k ? "bg-[#3E1F46] text-white border-[#3E1F46]" : "border-[#3E1F46] text-[#3E1F46] bg-white")}>{l}</button>
        ))}
      </div>
      {tab === "requests" && <BookingRequestsInbox />}
      {tab === "blocks" && <ClosedBlocksPanel />}
      {tab === "pricing" && <BookingPricingPanel />}

      <div className={tab === "bookings" ? "grid grid-cols-3 gap-3 max-w-lg" : "hidden"}>
        <Stat label="Bookings" value={data.totals.bookings} testid="private-stat-bookings" />
        <Stat label="Upcoming" value={data.totals.upcoming} testid="private-stat-upcoming" />
        <Stat label="Value" value={`NZ$${Number(data.totals.value).toFixed(0)}`} testid="private-stat-value" accent />
      </div>

      <PaymentSettings payment={data.payment} onSaved={load} />

      <section className="bg-white border border-[#E8DFCB] rounded p-4 space-y-3" data-testid="private-availability">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] inline-flex items-center gap-1.5">
          <CalendarDays className="w-4 h-4" /> Available dates &amp; times
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs">
            <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Date</span>
            <input type="date" value={slotDate} onChange={(e) => setSlotDate(e.target.value)} data-testid="private-slot-date"
              className="h-10 px-3 border border-[#E8DFCB] bg-white text-sm" />
          </label>
          <label className="text-xs flex-1 min-w-[220px]">
            <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Times (comma separated — 18:00 or later is an evening booking)</span>
            <input value={slotTimes} onChange={(e) => setSlotTimes(e.target.value)} placeholder="10:00, 14:00, 18:30"
              data-testid="private-slot-times" className="w-full h-10 px-3 border border-[#E8DFCB] bg-white text-sm" />
          </label>
          <button type="button" onClick={addSlots} disabled={busy === "slots"} data-testid="private-slot-add"
            className="h-10 px-4 bg-[#6E1E3A] text-white text-[11px] font-bold uppercase tracking-[0.14em] inline-flex items-center gap-1.5 disabled:opacity-40">
            {busy === "slots" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add times
          </button>
        </div>
        {slotsByDate.length === 0 ? (
          <p className="text-xs italic text-[#9C8B73]">No dates open yet.</p>
        ) : (
          <div className="space-y-2">
            {slotsByDate.map(([d, slots]) => (
              <div key={d} className="flex flex-wrap items-center gap-2" data-testid={`private-slot-day-${d}`}>
                <span className="text-sm font-serif text-[#3C2A1E] w-[190px]">{prettyDate(d)}</span>
                {slots.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full border text-xs font-semibold"
                    style={s.taken ? { borderColor: "#2F6B4F", color: "#2F6B4F" } : { borderColor: "#E8DFCB", color: "#3C2A1E" }}
                    data-testid={`private-slot-${s.id}`}>
                    <Clock className="w-3 h-3" /> {s.time}{s.evening ? " · evening" : ""}{s.taken ? " · booked" : ""}
                    {!s.taken && (
                      <button type="button" onClick={() => removeSlot(s)} disabled={busy === s.id} aria-label="Remove time"
                        data-testid={`private-slot-remove-${s.id}`} className="p-0.5 rounded-full hover:bg-[#A33030] hover:text-white">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white border border-[#E8DFCB] rounded p-4 space-y-3" data-testid="private-blocked">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#A33030] inline-flex items-center gap-1.5">
          <Ban className="w-4 h-4" /> Blocked dates
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <input type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} data-testid="private-block-date"
            className="h-10 px-3 border border-[#E8DFCB] bg-white text-sm" />
          <input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Reason (optional)"
            data-testid="private-block-reason" className="h-10 px-3 border border-[#E8DFCB] bg-white text-sm flex-1 min-w-[180px]" />
          <button type="button" onClick={block} disabled={busy === "block"} data-testid="private-block-add"
            className="h-10 px-4 border border-[#A33030] text-[#A33030] text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
            Block date
          </button>
        </div>
        {data.blocked_dates.length === 0 ? (
          <p className="text-xs italic text-[#9C8B73]">Nothing blocked.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {data.blocked_dates.map((b) => (
              <span key={b.id} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full border border-[#A33030] text-[#A33030] text-xs font-semibold" data-testid={`private-blocked-${b.date}`}>
                {prettyDate(b.date)}{b.reason ? ` · ${b.reason}` : ""}
                <button type="button" onClick={() => unblock(b)} disabled={busy === b.id} aria-label="Unblock" className="p-0.5 rounded-full hover:bg-[#A33030] hover:text-white">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Bookings</div>
        {data.bookings.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E8DFCB] p-10 text-center text-[#7A6A55] rounded" data-testid="private-bookings-empty">
            No private bookings yet.
          </div>
        ) : data.bookings.map((b) => {
          const s = STATUS_STYLE[b.status] || STATUS_STYLE.pending;
          return (
            <article key={b.id} className="bg-white border border-[#E8DFCB] rounded p-4" data-testid={`private-booking-${b.id}`}>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <strong className="font-serif text-[16px] text-[#3C2A1E]">{b.customer_name}</strong>
                <span className="text-xs text-[#9C8B73]">{b.reference}</span>
                <span className="text-xs text-[#7A6A55]">{b.email}{b.phone ? ` · ${b.phone}` : ""}</span>
                <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.16em] uppercase font-bold" style={{ backgroundColor: s.bg, color: s.fg }} data-testid={`private-status-${b.id}`}>{b.status}</span>
                <span className="ml-auto font-serif text-lg text-[#6E1E3A]" data-testid={`private-total-${b.id}`}>NZ${Number(b.total || 0).toFixed(2)}</span>
              </div>
              <div className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2 text-sm text-[#3C2A1E]">
                <div className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#9C8B73]" /> {prettyDate(b.date)}</div>
                <div className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#9C8B73]" /> {b.time}{b.evening ? " · evening" : ""}</div>
                <div className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#9C8B73]" /> {b.paid_guests} paid guests</div>
                <div className="inline-flex items-center gap-1.5"><Baby className="w-3.5 h-3.5 text-[#9C8B73]" /> {b.children} under 12 (free)</div>
              </div>
              <div className="mt-2 text-xs text-[#7A6A55] space-y-0.5">
                <div data-testid={`private-deposit-${b.id}`}>
                  50% deposit: <strong>NZ${Number(b.deposit_due ?? (b.total || 0) / 2).toFixed(2)}</strong>
                  {" "}· balance NZ${Number(b.balance_due ?? (b.total || 0) / 2).toFixed(2)} due 72hrs before
                </div>
                <div>Extras: {(b.extras || []).length ? b.extras.map((e) => `${e.label} ($${e.price})`).join(", ") : "none"}</div>
                {b.notes && <div className="italic">Customer notes: {b.notes}</div>}
                {b.admin_notes && <div className="italic text-[#A05A20]">Admin notes: {b.admin_notes}</div>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(data.statuses || []).map((st) => (
                  <button key={st} type="button" onClick={() => setStatus(b, st)} disabled={busy === b.id || b.status === st}
                    data-testid={`private-set-${st}-${b.id}`}
                    className="px-3 h-8 border border-[#E8DFCB] rounded text-[10px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E] hover:border-[#6E1E3A] disabled:opacity-30">
                    {st}
                  </button>
                ))}
                <button type="button" onClick={() => setEditing(b)} data-testid={`private-edit-${b.id}`}
                  className="px-3 h-8 bg-[#3E1F46] text-white rounded text-[10px] font-bold uppercase tracking-[0.14em]">Edit</button>
              </div>
            </article>
          );
        })}
      </section>

      {editing && (
        <EditBooking booking={editing} extras={data.extras} pricing={data.pricing} slots={data.slots}
          onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />
      )}
    </div>
  );
}

const Stat = ({ label, value, testid, accent }) => (
  <div className="bg-white border border-[#E8DFCB] rounded p-4">
    <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">{label}</div>
    <div className="font-serif text-3xl mt-1" style={{ color: accent ? "#6E1E3A" : "#3C2A1E" }} data-testid={testid}>{value}</div>
  </div>
);

function PaymentSettings({ payment, onSaved }) {
  const [stripeLink, setStripeLink] = useState(payment?.stripe_link || "");
  const [bank, setBank] = useState(payment?.bank_account || "");
  const [note, setNote] = useState(payment?.payment_note || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/private-bookings/settings", {
        stripe_link: stripeLink, bank_account: bank, payment_note: note,
      });
      toast.success("Payment details saved — customers see these when they book.");
      await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save those details."); }
    finally { setBusy(false); }
  };

  return (
    <section className="bg-white border border-[#E8DFCB] rounded p-4 space-y-3" data-testid="private-payment-settings">
      <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] inline-flex items-center gap-1.5">
        <CreditCard className="w-4 h-4" /> Deposit &amp; payment details
      </div>
      <p className="text-xs italic text-[#7A6A55]">
        50% deposit secures a booking (refundable outside 72 hours), balance due 72 hours before. These details show on
        the booking page and in the customer's HQ.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="text-xs">
          <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Stripe payment link</span>
          <input value={stripeLink} onChange={(e) => setStripeLink(e.target.value)} placeholder="https://buy.stripe.com/…"
            data-testid="private-stripe-input" className="w-full h-10 px-3 border border-[#E8DFCB] bg-white text-sm" />
        </label>
        <label className="text-xs">
          <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Direct debit / bank account</span>
          <input value={bank} onChange={(e) => setBank(e.target.value)} placeholder="Heritage Dahlias — 12-3456-7890123-00"
            data-testid="private-bank-input" className="w-full h-10 px-3 border border-[#E8DFCB] bg-white text-sm" />
        </label>
      </div>
      <label className="block text-xs">
        <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Payment note shown to customers</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="private-payment-note-input"
          className="w-full h-10 px-3 border border-[#E8DFCB] bg-white text-sm" />
      </label>
      <button type="button" onClick={save} disabled={busy} data-testid="private-payment-save"
        className="h-10 px-4 bg-[#6E1E3A] text-white text-[11px] font-bold uppercase tracking-[0.14em] inline-flex items-center gap-1.5 disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save payment details
      </button>
    </section>
  );
}

function EditBooking({ booking, extras, pricing, slots, onClose, onSaved }) {
  const [guests, setGuests] = useState(booking.paid_guests);
  const [children, setChildren] = useState(booking.children);
  const [keys, setKeys] = useState((booking.extras || []).map((e) => e.key));
  const [slotId, setSlotId] = useState(booking.slot_id || "");
  const [notes, setNotes] = useState(booking.notes || "");
  const [adminNotes, setAdminNotes] = useState(booking.admin_notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/private-bookings/bookings/${booking.id}`, {
        paid_guests: Number(guests), children: Number(children), extras: keys,
        slot_id: slotId || null, notes, admin_notes: adminNotes,
      });
      toast.success(`${booking.reference} updated.`);
      await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that booking."); }
    finally { setBusy(false); }
  };

  const price = (pricing || []).find((p) => p.paid_guests === Number(guests))?.price || 0;
  const extrasTotal = (extras || []).filter((e) => keys.includes(e.key)).reduce((n, e) => n + e.price, 0);

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 p-4 grid place-items-center" role="dialog" aria-modal="true" data-testid="private-edit-modal">
      <div className="w-full max-w-lg bg-[#FBF6E9] border shadow-2xl max-h-[92vh] overflow-y-auto" style={{ borderColor: "#C49247" }}>
        <header className="p-5 border-b flex items-start justify-between" style={{ borderColor: "#E8DFCB" }}>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">{booking.reference}</div>
            <h2 className="font-serif text-3xl mt-1 text-[#3C2A1E]">{booking.customer_name}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="private-edit-close"><X className="w-5 h-5" /></button>
        </header>
        <div className="p-5 space-y-4 text-sm">
          <label className="block text-xs">
            <span className="block text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Date &amp; time</span>
            <select value={slotId} onChange={(e) => setSlotId(e.target.value)} data-testid="private-edit-slot"
              className="w-full h-10 px-3 border bg-white text-sm" style={{ borderColor: "#E8DFCB" }}>
              <option value="">Keep {prettyDate(booking.date)} at {booking.time}</option>
              {(slots || []).filter((s) => !s.taken || s.id === booking.slot_id).map((s) => (
                <option key={s.id} value={s.id}>{prettyDate(s.date)} · {s.time}{s.evening ? " (evening)" : ""}</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs">
              <span className="block text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Paid guests</span>
              <select value={guests} onChange={(e) => setGuests(e.target.value)} data-testid="private-edit-guests"
                className="w-full h-10 px-3 border bg-white text-sm" style={{ borderColor: "#E8DFCB" }}>
                {(pricing || []).map((p) => <option key={p.paid_guests} value={p.paid_guests}>{p.paid_guests} — ${p.price}</option>)}
              </select>
            </label>
            <label className="block text-xs">
              <span className="block text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Children under 12</span>
              <input type="number" min="0" value={children} onChange={(e) => setChildren(e.target.value)} data-testid="private-edit-children"
                className="w-full h-10 px-3 border bg-white text-sm" style={{ borderColor: "#E8DFCB" }} />
            </label>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Extras</div>
            <div className="space-y-1.5">
              {(extras || []).map((e) => (
                <label key={e.key} className="flex items-center gap-2 text-sm" data-testid={`private-edit-extra-${e.key}`}>
                  <input type="checkbox" checked={keys.includes(e.key)}
                    onChange={() => setKeys((prev) => prev.includes(e.key) ? prev.filter((k) => k !== e.key) : [...prev, e.key])} />
                  {e.label} <span className="ml-auto text-[#6E1E3A] font-semibold">${e.price}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="block text-xs">
            <span className="block text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Customer notes</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="private-edit-notes"
              className="w-full p-3 border bg-white text-sm" style={{ borderColor: "#E8DFCB" }} />
          </label>
          <label className="block text-xs">
            <span className="block text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Admin notes</span>
            <textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={2} data-testid="private-edit-admin-notes"
              className="w-full p-3 border bg-white text-sm" style={{ borderColor: "#E8DFCB" }} />
          </label>
          <div className="flex items-center justify-between gap-3 border-t pt-4" style={{ borderColor: "#E8DFCB" }}>
            <div className="font-serif text-2xl text-[#6E1E3A]" data-testid="private-edit-total">NZ${(price + extrasTotal).toFixed(2)}</div>
            <button type="button" onClick={save} disabled={busy} data-testid="private-edit-save"
              className="h-10 px-5 bg-[#6E1E3A] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
